/**
 * Legal Document Modal
 * 
 * Displays Terms and Conditions or Privacy Policy in a modal
 */

import React from 'react';
import './LegalDocumentModal.css';

interface LegalDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: 'terms' | 'privacy';
}

export function LegalDocumentModal({ isOpen, onClose, documentType }: LegalDocumentModalProps): React.ReactElement | null {
  if (!isOpen) return null;

  const title = documentType === 'terms' ? 'Terms and Conditions' : 'Privacy Policy';
  
  return (
    <div className="legal-modal-overlay" onClick={onClose}>
      <div className="legal-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="legal-modal-header">
          <h2>{title}</h2>
          <button className="legal-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="legal-modal-body">
          {documentType === 'terms' ? <TermsContent /> : <PrivacyContent />}
        </div>
        <div className="legal-modal-footer">
          <button className="legal-modal-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function TermsContent(): React.ReactElement {
  return (
    <div className="legal-document">
      <p className="legal-date"><strong>Last Updated:</strong> December 13, 2025</p>
      <p className="legal-date"><strong>Effective Date:</strong> December 13, 2025</p>

      <section>
        <h3>1. ACCEPTANCE OF TERMS</h3>
        <p>By creating an account, accessing, or using Brynlore ("the Game"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you may not access or use the Game.</p>
        <p>Brynlore is operated by <strong>Loach Design</strong> ("we," "us," or "our").</p>
      </section>

      <section>
        <h3>2. DESCRIPTION OF SERVICE</h3>
        <p>Brynlore is a multiplayer online exploration game featuring:</p>
        <ul>
          <li>AI-generated narrative quests</li>
          <li>Hex-based world exploration</li>
          <li>Combat and monster encounters</li>
          <li>Community-driven lore discovery system</li>
        </ul>
      </section>

      <section>
        <h3>3. ACCOUNT REGISTRATION & ELIGIBILITY</h3>
        <p><strong>3.1 Eligibility:</strong> You must be at least 13 years of age to create an account. Users under 18 require parental or guardian consent.</p>
        <p><strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account.</p>
        <p><strong>3.3 Account Accuracy:</strong> You must provide accurate, current, and complete information during registration and keep your account information updated.</p>
        <p><strong>3.4 One Account Per User:</strong> Each user may maintain only one account unless explicitly authorized by us.</p>
      </section>

      <section>
        <h3>4. CONTENT USAGE AND INTELLECTUAL PROPERTY RIGHTS</h3>
        <p><strong>4.1 Grant of Content License</strong></p>
        <p>By playing Brynlore, you grant us a perpetual, irrevocable, worldwide, royalty-free, non-exclusive license to:</p>
        <ul>
          <li>Record, capture, and archive your gameplay choices and narrative outcomes</li>
          <li>Create derivative works including podcasts, videos, written articles, and social media content based on your gameplay</li>
          <li>Publish, distribute, broadcast, and publicly display such content across any media platform (YouTube, podcasts, social media, websites, etc.)</li>
          <li>Edit, modify, adapt, and remix your gameplay content for entertainment and promotional purposes</li>
        </ul>
        
        <p><strong>4.2 Anonymized Content Creation</strong></p>
        <p>We reserve the right to create podcasts, videos, and other content based on gameplay within Brynlore. When we create such content:</p>
        <ul>
          <li>We will <strong>NOT</strong> use your real name, username, email, or any personally identifying information without your explicit written consent</li>
          <li>Gameplay narratives will be presented anonymously (e.g., "a player encountered," "the adventurer chose")</li>
          <li>Your privacy and anonymity will be maintained in all published content unless you separately agree to be identified</li>
        </ul>

        <p><strong>4.3 User-Generated Content</strong></p>
        <p>Any content you submit to the Game (usernames, messages, feedback) may be used by us as described in Section 4.1.</p>

        <p><strong>4.4 Our Intellectual Property</strong></p>
        <p>All content within the Game—including but not limited to text, graphics, code, storylines, characters, music, lore, and game mechanics—is our exclusive property or licensed to us. You may not copy, reproduce, distribute, or create derivative works without our written permission.</p>
      </section>

      <section>
        <h3>5. USER CONDUCT</h3>
        <p>You agree NOT to:</p>
        <ul>
          <li>Use cheats, exploits, automation software, bots, hacks, mods, or unauthorized third-party software</li>
          <li>Harass, threaten, or harm other players</li>
          <li>Use offensive, obscene, or inappropriate usernames or content</li>
          <li>Share account credentials with others</li>
          <li>Attempt to gain unauthorized access to the Game or other users' accounts</li>
          <li>Reverse engineer, decompile, or disassemble any part of the Game</li>
          <li>Engage in real-money trading of in-game items or accounts outside our systems</li>
        </ul>
        <p>Violation of these rules may result in account suspension or termination.</p>
      </section>

      <section>
        <h3>6. DISCLAIMER OF WARRANTIES</h3>
        <p>THE GAME IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:</p>
        <ul>
          <li>Warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
          <li>Uninterrupted, secure, or error-free operation</li>
          <li>Accuracy or reliability of AI-generated content</li>
        </ul>
        <p>We do not guarantee that the Game will meet your requirements or be available at all times.</p>
      </section>

      <section>
        <h3>7. LIMITATION OF LIABILITY</h3>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR:</p>
        <ul>
          <li>Any indirect, incidental, consequential, special, or punitive damages</li>
          <li>Loss of profits, data, or goodwill</li>
          <li>Damages resulting from your use or inability to use the Game</li>
          <li>Any content created or published based on your gameplay</li>
        </ul>
        <p><strong>Total Liability Cap:</strong> Our total liability to you shall not exceed £100.</p>
      </section>

      <section>
        <h3>8. INDEMNIFICATION</h3>
        <p>You agree to indemnify, defend, and hold us harmless from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:</p>
        <ul>
          <li>Your violation of these Terms</li>
          <li>Your use of the Game</li>
          <li>Your violation of any third-party rights</li>
          <li>Content you submit to the Game</li>
        </ul>
      </section>

      <section>
        <h3>9. TERMINATION</h3>
        <p><strong>9.1 By You:</strong> You may terminate your account at any time by contacting us at dan.loach@loachdesign.co.uk.</p>
        <p><strong>9.2 By Us:</strong> We may suspend or terminate your account immediately if you violate these Terms or for any reason at our discretion.</p>
        <p><strong>9.3 Effect of Termination:</strong> Upon termination, your right to use the Game ceases immediately. Sections 4, 7, 8, 9, and 10 survive termination.</p>
      </section>

      <section>
        <h3>10. CHANGES TO TERMS</h3>
        <p>We reserve the right to modify these Terms at any time. We will notify you of material changes via email or in-game notification. Your continued use of the Game after changes constitutes acceptance of the new Terms.</p>
      </section>

      <section>
        <h3>11. DISPUTE RESOLUTION</h3>
        <p><strong>11.1 Governing Law</strong></p>
        <p>These Terms are governed by the laws of <strong>England and Wales, United Kingdom</strong>, without regard to conflict of law principles.</p>
        <p><strong>11.2 Dispute Resolution</strong></p>
        <p>Any disputes arising from these Terms or your use of the Game shall be resolved in the courts of <strong>England and Wales, United Kingdom</strong>.</p>
        <p><strong>11.3 Class Action Waiver</strong></p>
        <p>You agree to resolve disputes with us only on an individual basis and waive your right to participate in class actions or class arbitrations.</p>
      </section>

      <section>
        <h3>12. MISCELLANEOUS</h3>
        <p><strong>12.1 Entire Agreement:</strong> These Terms constitute the entire agreement between you and us regarding the Game.</p>
        <p><strong>12.2 Severability:</strong> If any provision is found unenforceable, the remaining provisions remain in full effect.</p>
        <p><strong>12.3 No Waiver:</strong> Our failure to enforce any provision does not waive our right to enforce it later.</p>
        <p><strong>12.4 Assignment:</strong> We may assign these Terms to any successor or affiliate. You may not assign your rights without our written consent.</p>
        <p><strong>12.5 Force Majeure:</strong> We are not liable for delays or failures caused by circumstances beyond our reasonable control.</p>
      </section>

      <section>
        <h3>13. CONTACT INFORMATION</h3>
        <p>For questions about these Terms, please contact us at:</p>
        <p>
          <strong>Loach Design</strong><br />
          Email: dan.loach@loachdesign.co.uk<br />
          Website: https://brynlore.com
        </p>
      </section>
    </div>
  );
}

function PrivacyContent(): React.ReactElement {
  return (
    <div className="legal-document">
      <p className="legal-date"><strong>Last Updated:</strong> December 13, 2025</p>
      <p className="legal-date"><strong>Effective Date:</strong> December 13, 2025</p>

      <section>
        <h3>1. INTRODUCTION</h3>
        <p>This Privacy Policy explains how <strong>Loach Design</strong> ("we," "us," or "our") collects, uses, discloses, and protects information when you use Brynlore ("the Game").</p>
        <p>By creating an account and using Brynlore, you consent to the data practices described in this Privacy Policy.</p>
      </section>

      <section>
        <h3>2. INFORMATION WE COLLECT</h3>
        
        <h4>2.1 Account Information</h4>
        <p>When you create an account, we collect:</p>
        <ul>
          <li><strong>Email address</strong> (for authentication and account recovery)</li>
          <li><strong>Display name</strong> (your in-game character name)</li>
          <li><strong>Password</strong> (stored securely using Firebase Authentication)</li>
        </ul>

        <h4>2.2 Gameplay Data</h4>
        <p>We automatically collect and store:</p>
        <ul>
          <li><strong>Game progress:</strong> hex exploration, world state, discovered locations</li>
          <li><strong>Quest data:</strong> choices made, quest outcomes, narrative branches taken</li>
          <li><strong>Combat records:</strong> battles fought, monsters encountered, victory/defeat outcomes</li>
          <li><strong>Lore discoveries:</strong> folklore and lore entries you've unlocked</li>
          <li><strong>Player statistics:</strong> health, experience points, inventory, character progression</li>
          <li><strong>Session information:</strong> timestamps, play duration, game version</li>
        </ul>

        <h4>2.3 Technical Data</h4>
        <p>We may collect:</p>
        <ul>
          <li><strong>Device information:</strong> browser type, operating system, device type</li>
          <li><strong>IP address:</strong> for security and fraud prevention</li>
          <li><strong>Error logs:</strong> crash reports and bug diagnostics</li>
          <li><strong>Performance metrics:</strong> load times, API response times</li>
        </ul>

        <h4>2.4 Information We Do NOT Collect</h4>
        <ul>
          <li>We do not collect payment information (the Game is currently free)</li>
          <li>We do not use tracking cookies or third-party analytics (beyond Firebase)</li>
          <li>We do not collect precise geolocation data</li>
          <li>We do not access your device's camera, microphone, or contacts</li>
        </ul>
      </section>

      <section>
        <h3>3. HOW WE USE YOUR INFORMATION</h3>
        
        <h4>3.1 To Provide Game Services</h4>
        <ul>
          <li>Create and manage your account</li>
          <li>Save your game progress and character state</li>
          <li>Enable multiplayer features (shared world state, lore discovery)</li>
          <li>Generate personalized AI-driven quests based on your choices</li>
        </ul>

        <h4>3.2 To Create Content (Anonymously)</h4>
        <p>As outlined in our Terms and Conditions (Section 4), we may use your gameplay data to:</p>
        <ul>
          <li>Create podcasts narrating player adventures</li>
          <li>Produce videos showcasing gameplay scenarios</li>
          <li>Write articles or social media posts about interesting quest outcomes</li>
          <li>Generate aggregated statistics about player behavior</li>
        </ul>
        <p><strong>Important:</strong> When we create content from your gameplay:</p>
        <ul>
          <li>We will <strong>NEVER</strong> use your email, username, or any personally identifying information without your explicit written consent</li>
          <li>All gameplay narratives will be anonymized (e.g., "a player chose to..." or "the adventurer discovered...")</li>
          <li>Your privacy is our priority</li>
        </ul>

        <h4>3.3 To Improve the Game</h4>
        <ul>
          <li>Analyze gameplay patterns to improve game balance</li>
          <li>Identify and fix bugs and technical issues</li>
          <li>Develop new features and content</li>
          <li>Optimize AI-generated narrative quality</li>
        </ul>

        <h4>3.4 Communications</h4>
        <ul>
          <li>Send account verification emails</li>
          <li>Notify you of password reset requests</li>
          <li>Send important service announcements (e.g., Terms updates)</li>
          <li>Respond to your support inquiries</li>
        </ul>
        <p><strong>Note:</strong> We will not send marketing emails without your explicit opt-in consent.</p>
      </section>

      <section>
        <h3>4. HOW WE STORE AND PROTECT YOUR DATA</h3>
        
        <h4>4.1 Data Storage</h4>
        <p>Your data is stored using:</p>
        <ul>
          <li><strong>Firebase Authentication</strong> (for account credentials)</li>
          <li><strong>Cloud Firestore</strong> (for game state and progress)</li>
          <li><strong>Google Cloud Platform</strong> infrastructure</li>
        </ul>
        <p>All data is stored on servers located in <strong>Europe (via Google Cloud Platform)</strong>.</p>

        <h4>4.2 Security Measures</h4>
        <p>We implement industry-standard security practices:</p>
        <ul>
          <li>Passwords are hashed and encrypted</li>
          <li>Data transmission uses HTTPS/TLS encryption</li>
          <li>Database access is restricted and authenticated</li>
          <li>Regular security updates and patches</li>
        </ul>
        <p><strong>However:</strong> No system is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.</p>

        <h4>4.3 Data Retention</h4>
        <ul>
          <li><strong>Active accounts:</strong> We retain your data indefinitely while your account is active</li>
          <li><strong>Inactive accounts:</strong> Accounts inactive for 2+ years may be archived or deleted</li>
          <li><strong>Deleted accounts:</strong> Upon deletion request, we will remove your personal data within 30 days (excluding data required for legal compliance)</li>
          <li><strong>Gameplay narratives:</strong> Anonymized gameplay content used in podcasts/videos may be retained permanently</li>
        </ul>
      </section>

      <section>
        <h3>5. DATA SHARING AND DISCLOSURE</h3>
        
        <h4>5.1 We Do NOT Sell Your Data</h4>
        <p>We will never sell, rent, or trade your personal information to third parties for marketing purposes.</p>

        <h4>5.2 Service Providers</h4>
        <p>We share data with trusted service providers who help us operate the Game:</p>
        <ul>
          <li><strong>Firebase/Google Cloud</strong> (hosting and authentication)</li>
          <li><strong>OpenAI</strong> (AI-generated quest narratives - only gameplay context is sent, never personal info)</li>
          <li><strong>ElevenLabs</strong> (text-to-speech for narration - only quest text is sent, never personal info)</li>
        </ul>
        <p>These providers are contractually required to protect your data and may not use it for other purposes.</p>

        <h4>5.3 Legal Requirements</h4>
        <p>We may disclose your information if required by law, court order, or governmental request, or to:</p>
        <ul>
          <li>Comply with legal obligations</li>
          <li>Protect our rights, property, or safety</li>
          <li>Prevent fraud or illegal activity</li>
          <li>Enforce our Terms and Conditions</li>
        </ul>

        <h4>5.4 Business Transfers</h4>
        <p>If we are acquired, merge with another company, or sell assets, your data may be transferred to the new entity.</p>
      </section>

      <section>
        <h3>6. YOUR PRIVACY RIGHTS</h3>
        
        <h4>6.1 Access and Correction</h4>
        <p>You may:</p>
        <ul>
          <li>View your account information in the game's Account Settings</li>
          <li>Update your display name at any time</li>
          <li>Request a copy of your data by contacting us at <strong>dan.loach@loachdesign.co.uk</strong></li>
        </ul>

        <h4>6.2 Account Deletion</h4>
        <p>You may request account deletion at any time by emailing <strong>dan.loach@loachdesign.co.uk</strong>. Upon deletion:</p>
        <ul>
          <li>Your email, username, and personal data will be removed within 30 days</li>
          <li>Your gameplay contributions to the shared world (hex exploration, lore discoveries) may remain for game integrity</li>
          <li>Anonymized gameplay narratives in published content (podcasts, videos) will remain</li>
        </ul>

        <h4>6.3 Opt-Out Rights</h4>
        <p>You may opt out of:</p>
        <ul>
          <li>Email notifications (except critical account security emails)</li>
          <li>Having your gameplay featured in content (contact us to request exclusion from future content creation)</li>
        </ul>

        <h4>6.4 Additional Rights (EU/UK Users - GDPR)</h4>
        <p>If you are in the European Economic Area or UK, you have additional rights:</p>
        <ul>
          <li><strong>Right to erasure</strong> ("right to be forgotten")</li>
          <li><strong>Right to data portability</strong> (receive your data in machine-readable format)</li>
          <li><strong>Right to object</strong> to processing</li>
          <li><strong>Right to restrict processing</strong></li>
          <li><strong>Right to lodge a complaint</strong> with your data protection authority</li>
        </ul>
        <p>Contact us at <strong>dan.loach@loachdesign.co.uk</strong> to exercise these rights.</p>
      </section>

      <section>
        <h3>7. CHILDREN'S PRIVACY (COPPA COMPLIANCE)</h3>
        <p>Brynlore is intended for users <strong>13 years of age and older</strong>.</p>
        <p>We do not knowingly collect personal information from children under 13. If you believe a child under 13 has created an account, please contact us immediately at <strong>dan.loach@loachdesign.co.uk</strong>, and we will delete the account.</p>
        <p><strong>For users 13-17:</strong> We recommend obtaining parental/guardian consent before creating an account.</p>
      </section>

      <section>
        <h3>8. INTERNATIONAL DATA TRANSFERS</h3>
        <p>If you access Brynlore from outside <strong>the United Kingdom</strong>, your data will be transferred to and stored on servers in <strong>Europe (via Google Cloud Platform)</strong>. By using the Game, you consent to this transfer.</p>
        <p>We comply with applicable data protection laws for international transfers.</p>
      </section>

      <section>
        <h3>9. CHANGES TO THIS PRIVACY POLICY</h3>
        <p>We may update this Privacy Policy from time to time. When we make material changes, we will:</p>
        <ul>
          <li>Update the "Last Updated" date at the top</li>
          <li>Notify you via email or in-game notification</li>
          <li>Post the new policy on our website</li>
        </ul>
        <p>Your continued use of the Game after changes constitutes acceptance of the updated Privacy Policy.</p>
      </section>

      <section>
        <h3>10. CONTACT US</h3>
        <p>If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:</p>
        <p>
          <strong>Loach Design</strong><br />
          Email: <strong>dan.loach@loachdesign.co.uk</strong><br />
          Website: https://brynlore.com
        </p>
      </section>
    </div>
  );
}



